import { CanonicalBusinessEvent } from "../types/cbes";

export interface BundleRevenueTimelineEntry {
  date: string;
  productId: string;
  eventType: "prepaid" | "fulfilled" | "pending";
  amount: number;
  currencyCode: string;
  deferredBalance: number;
  recognizedTotal: number;
}

export interface BundleRevenueSummary {
  totalOrderValue: number;
  deferredRevenue: number;
  recognizedRevenue: number;
  completionPercentage: number;
  fulfilledCount: number;
  totalCount: number;
  currencyCode: string;
}

interface BundleEvent {
  usecaseName: string;
  transactionDate: string;
  asin: string;
  amount: number;
  currencyCode: string;
  productIdentifier: string;
  version: number;
}

const extractProductIdentifier = (idempotenceId: string): string => {
  const match = idempotenceId.match(/amzn1\.dv\.gti\.[a-f0-9-]+/i);
  return match ? match[0] : idempotenceId;
};

export const isBundleOrder = (cbe: CanonicalBusinessEvent): boolean => {
  const attrs = cbe.internalAttributes;
  return (
    attrs?.IS_BUNDLE === "true" ||
    attrs?.IS_BUNDLE_IMMEDIATE === "true" ||
    attrs?.IS_SEASON_PASS_BUNDLE === "true" ||
    attrs?.IS_PREORDER_BUNDLE === "true"
  );
};

export const hasBundleOrder = (cbes: CanonicalBusinessEvent[]): boolean =>
  cbes.some(isBundleOrder);

const extractBundleEvents = (cbes: CanonicalBusinessEvent[]): BundleEvent[] => {
  const eventsByProduct = new Map<string, BundleEvent>();

  cbes.forEach((cbe) => {
    const { usecaseName } = cbe;
    if (
      usecaseName !== "PrepaidChargeSuccess" &&
      usecaseName !== "PaidFulfillment"
    )
      return;

    const productIdentifier =
      usecaseName === "PrepaidChargeSuccess"
        ? `prepaid-${cbe.orderId}`
        : extractProductIdentifier(cbe.idempotenceId);

    let amount = 0;
    let currencyCode = "USD";

    if (usecaseName === "PaidFulfillment" && cbe.records) {
      const recordsArray = Array.isArray(cbe.records)
        ? cbe.records
        : Object.values(cbe.records);
      const creditRecord = recordsArray.find(
        (r) =>
          r["@type"] === "SimpleCBERecord" &&
          (r.content || r).entryType === "CREDIT"
      );

      if (creditRecord) {
        const content = creditRecord.content || creditRecord;
        const functionalAmount = content.functionalAmount;
        if (functionalAmount) {
          amount = parseFloat(functionalAmount.value || "0");
          currencyCode = functionalAmount.currencyCode || "USD";
        }
      }
    }

    const version = cbe.idempotenceVersion || 0;
    const existing = eventsByProduct.get(productIdentifier);
    if (!existing || version > existing.version) {
      eventsByProduct.set(productIdentifier, {
        usecaseName,
        transactionDate: cbe.transactionDate,
        asin: cbe.asin,
        amount,
        currencyCode,
        productIdentifier,
        version,
      });
    }
  });

  return Array.from(eventsByProduct.values());
};

export const generateBundleRevenueTimeline = (
  cbes: CanonicalBusinessEvent[],
  totalOrderValueFromPayment?: number,
  _dbdProductCount?: number,
  dbdProducts?: { [key: string]: any }
): BundleRevenueTimelineEntry[] => {
  const bundleEvents = extractBundleEvents(cbes);
  if (!bundleEvents.length) return [];

  const prepaidEvents = bundleEvents.filter(
    (e) => e.usecaseName === "PrepaidChargeSuccess"
  );
  const fulfillmentEvents = bundleEvents.filter(
    (e) => e.usecaseName === "PaidFulfillment"
  );

  const totalOrderValue =
    totalOrderValueFromPayment ||
    prepaidEvents.reduce((sum, e) => sum + e.amount, 0) ||
    fulfillmentEvents.reduce((sum, e) => sum + e.amount, 0);

  const currencyCode =
    prepaidEvents[0]?.currencyCode ||
    fulfillmentEvents[0]?.currencyCode ||
    "USD";
  const timeline: BundleRevenueTimelineEntry[] = [];

  if (prepaidEvents.length > 0 || totalOrderValueFromPayment) {
    const earliestDate =
      prepaidEvents.length > 0
        ? Math.min(
            ...prepaidEvents.map((e) => new Date(e.transactionDate).getTime())
          )
        : new Date(
            fulfillmentEvents[0]?.transactionDate || Date.now()
          ).getTime();

    timeline.push({
      date: new Date(earliestDate).toISOString(),
      productId: "Bundle Order",
      eventType: "prepaid",
      amount: totalOrderValue,
      currencyCode,
      deferredBalance: totalOrderValue,
      recognizedTotal: 0,
    });
  }

  fulfillmentEvents.sort(
    (a, b) =>
      new Date(a.transactionDate).getTime() -
      new Date(b.transactionDate).getTime()
  );

  let runningDeferred = totalOrderValue;
  let runningRecognized = 0;
  const fulfilledIds = new Set<string>();

  fulfillmentEvents.forEach((event) => {
    runningDeferred = Math.max(0, runningDeferred - event.amount);
    runningRecognized += event.amount;
    fulfilledIds.add(event.productIdentifier);

    timeline.push({
      date: event.transactionDate,
      productId: event.productIdentifier,
      eventType: "fulfilled",
      amount: event.amount,
      currencyCode: event.currencyCode,
      deferredBalance: runningDeferred,
      recognizedTotal: runningRecognized,
    });
  });

  if (dbdProducts) {
    for (const productKey in dbdProducts) {
      const isAlreadyFulfilled = Array.from(fulfilledIds).some(
        (id) => id.includes(productKey) || productKey.includes(id)
      );

      if (!isAlreadyFulfilled) {
        timeline.push({
          date: new Date().toISOString(),
          productId: productKey,
          eventType: "pending",
          amount: 0,
          currencyCode,
          deferredBalance: runningDeferred,
          recognizedTotal: runningRecognized,
        });
      }
    }
  }

  return timeline;
};

export const calculateBundleRevenueSummary = (
  cbes: CanonicalBusinessEvent[],
  totalOrderValueFromPayment?: number,
  dbdProductCount?: number
): BundleRevenueSummary | null => {
  const bundleEvents = extractBundleEvents(cbes);
  if (!bundleEvents.length) return null;

  const prepaidEvents = bundleEvents.filter(
    (e) => e.usecaseName === "PrepaidChargeSuccess"
  );
  const fulfillmentEvents = bundleEvents.filter(
    (e) => e.usecaseName === "PaidFulfillment"
  );

  const totalOrderValue =
    totalOrderValueFromPayment ||
    prepaidEvents.reduce((sum, e) => sum + e.amount, 0) ||
    fulfillmentEvents.reduce((sum, e) => sum + e.amount, 0);

  const recognizedRevenue = fulfillmentEvents.reduce(
    (sum, e) => sum + e.amount,
    0
  );
  const deferredRevenue = Math.max(0, totalOrderValue - recognizedRevenue);

  const fulfilledCount = new Set(
    fulfillmentEvents.map((e) => e.productIdentifier)
  ).size;
  const totalCount = dbdProductCount || fulfilledCount;
  const completionPercentage =
    totalCount > 0 ? (fulfilledCount / totalCount) * 100 : 0;
  const currencyCode =
    prepaidEvents[0]?.currencyCode ||
    fulfillmentEvents[0]?.currencyCode ||
    "USD";

  return {
    totalOrderValue,
    deferredRevenue,
    recognizedRevenue,
    completionPercentage,
    fulfilledCount,
    totalCount,
    currencyCode,
  };
};

export const hasMultipleProducts = (
  cbes: CanonicalBusinessEvent[]
): boolean => {
  const productIds = new Set(
    extractBundleEvents(cbes)
      .filter((e) => e.usecaseName === "PaidFulfillment")
      .map((e) => e.productIdentifier)
  );
  return productIds.size > 1;
};
